"use client";

import { useState } from "react";
import { showcaseExamples } from "@/lib/examples";
import QosmCode from "./QosmCode";
import { accent } from "@/lib/palette";

export default function CodeShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const example = showcaseExamples[activeTab];

  return (
    <section id="examples" className="py-24 px-4 bg-bg-elevated max-md:py-16">
      <div className="max-w-[1000px] mx-auto">
        <div className="text-center mb-12 max-md:mb-8">
          <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-text-primary tracking-tight mb-4">
            Capabilities are just <span style={{ color: "#6ee7b7" }}>functions</span>.
          </h2>
          <p className="text-lg text-text-secondary max-w-[600px] mx-auto leading-relaxed max-md:text-base">
            The host grants typed functions to the agent. The agent can call them, compose them, pass them around, but it can never invent new ones.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-border max-md:overflow-x-auto max-md:gap-0">
          {showcaseExamples.map((ex, i) => (
            <button
              key={ex.id}
              onClick={() => setActiveTab(i)}
              className={`
                px-5 py-3 text-sm font-medium transition-colors border-b-2 bg-transparent cursor-pointer whitespace-nowrap
                max-md:px-4 max-md:text-xs max-md:flex-1
                ${i === activeTab
                  ? "text-text-primary border-transparent"
                  : "text-text-muted border-transparent hover:text-text-secondary"
                }
              `}
              style={i === activeTab ? { borderBottomColor: accent.DEFAULT } : undefined}
            >
              {ex.tab}
            </button>
          ))}
        </div>

        {/* Code block + Capabilities panel */}
        <div className="flex gap-4 items-stretch max-md:flex-col max-md:gap-3">
          {/* Code panel */}
          <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-border">
            <div className="bg-bg-surface px-4 py-2.5 flex justify-between items-center border-b border-border">
              <span className="font-mono text-xs text-text-muted">{example.id}.qosm</span>
              <span className="text-[0.6rem] font-semibold uppercase tracking-widest text-text-muted">
                Qosm
              </span>
            </div>

            {/* Host prelude (if present) */}
            {example.prelude && (
              <div className="bg-bg-surface/60 relative">
                <div className="absolute top-1.5 right-3">
                  <span className="text-[8px] font-semibold uppercase tracking-widest text-text-dim">
                    Provided by host
                  </span>
                </div>
                <QosmCode
                  code={example.prelude}
                  className="m-0 px-5 pt-3 pb-2 text-[13px] leading-relaxed overflow-x-auto opacity-50 max-md:text-sm max-md:px-4"
                />
              </div>
            )}

            {/* Divider + agent code */}
            <div className="bg-bg-surface min-h-[200px] max-md:min-h-0">
              {example.prelude && (
                <div className="flex items-center gap-2 px-5 pt-1">
                  <span className="flex-1 h-px bg-border" />
                  <span className="text-[8px] font-semibold uppercase tracking-widest text-text-dim">Agent code</span>
                  <span className="flex-1 h-px bg-border" />
                </div>
              )}
              <QosmCode
                code={example.code}
                className={`m-0 px-5 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-sm max-md:px-4 ${example.prelude ? "pt-2" : "pt-4"}`}
              />
            </div>

            <div className="px-5 py-3 bg-bg-primary border-t border-border">
              <p className="text-xs text-text-muted leading-relaxed max-md:text-[11px]">
                {example.annotation}
              </p>
            </div>
          </div>

          {/* Capabilities panel */}
          <div className="w-[260px] shrink-0 rounded-xl border border-border bg-bg-elevated flex flex-col max-md:w-full">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent.DEFAULT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Capabilities
              </span>
            </div>
            <div className="flex-1 flex flex-col gap-0 divide-y divide-bg-surface">
              {example.capabilities.map((cap) => (
                <div key={cap.extern} className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: cap.color }}
                    />
                    <span className="font-mono text-xs font-semibold" style={{ color: cap.color }}>
                      {cap.label}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 pl-3.5">
                    <div className="font-mono text-[11px] text-text-secondary">
                      {cap.extern}
                    </div>
                    {cap.attenuations.map((att) => (
                      <div key={att.key} className="flex items-baseline gap-1.5 font-mono text-[11px]">
                        <span className="text-text-dim">{att.key}:</span>
                        <span className="text-text-muted">{att.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-border">
              <p className="text-[10px] text-text-dim leading-relaxed">
                The host attenuates each capability before granting it to the agent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
